export interface PanelNavMetricEntry {
    navId: string | null
    name: string
    timeMs: number
    elapsedMs?: number
    detail?: unknown
}

export interface PanelNavInputEventLike {
    timeStamp?: number
    type?: string
    nativeEvent?: PanelNavInputEventLike
}

export interface PanelNavInputDetail {
    inputTimeMs?: number
    inputType?: string
}

interface PanelNavLongTaskSummary {
    count: number
    totalDurationMs: number
    worstDurationMs: number
    entries: Array<{
        startTimeMs: number
        durationMs: number
    }>
}

export interface PanelNavMetricSpanSnapshot {
    navId: string
    source: string
    startedAtMs: number
    inputTimeMs?: number
    inputDelayMs?: number
    finishedAtMs?: number
    durationMs?: number
    inputToSettledDelayMs?: number
    firstPanelMoveDelayMs?: number
    inputToFirstPanelMoveDelayMs?: number
    routeChangeDelayMs?: number
    inputToRouteChangeDelayMs?: number
    transitionEndDelayMs?: number
    inputToTransitionEndDelayMs?: number
    questSettledDelayMs?: number
    inputToQuestSettledDelayMs?: number
    finishReason?: string
    detail?: unknown
    marks: PanelNavMetricEntry[]
    longTasks: PanelNavLongTaskSummary
}

export interface PanelNavMetricsStore {
    marks: PanelNavMetricEntry[]
    spans: PanelNavMetricSpanSnapshot[]
    activeSpan: PanelNavMetricSpanSnapshot | null
}

interface ActivePanelNavSpan {
    navId: string
    source: string
    startedAtMs: number
    inputTimeMs?: number
    finishedAtMs?: number
    firstPanelMoveAtMs?: number
    routeChangeAtMs?: number
    transitionEndAtMs?: number
    questSettledAtMs?: number
    expectedQuestId?: string
    questSettledQuestId?: string
    finishReason?: string
    detail?: unknown
    marks: PanelNavMetricEntry[]
    longTasks: PanelNavLongTaskSummary
    longTaskKeys: Set<string>
}

const DEFAULT_QUIET_MS = 400
const DEFAULT_NAV_TIMEOUT_MS = 10000
const TRANSITION_END_FALLBACK_MS = 1600
const MAX_LONG_TASK_ENTRIES = 25
const MAX_STORED_MARKS = 500
const MAX_STORED_SPANS = 100

const PANEL_NAV_INPUT_METHODS: Record<string, string> = {
    'header-arrow': 'header_arrow',
    'swiper-touch': 'touch_swipe',
    'wheel-swipe': 'trackpad_swipe',
}

let nextNavId = 1
let activeSpan: ActivePanelNavSpan | null = null
let longTaskObserver: PerformanceObserver | null = null
let mutationObserver: MutationObserver | null = null
let quietTimer: number | null = null
let timeoutTimer: number | null = null
let transitionEndFallbackTimer: number | null = null

function roundMs(value: number) {
    return Math.round(value * 10) / 10
}

function normalizedInputTimeMs(
    timeStamp: unknown,
    now = typeof performance !== 'undefined' ? performance.now() : 0,
) {
    if (typeof timeStamp !== 'number' || !Number.isFinite(timeStamp) || timeStamp <= 0) {
        return undefined
    }

    let inputTimeMs = timeStamp
    const timeOrigin = typeof performance !== 'undefined' ? performance.timeOrigin : undefined
    if (
        typeof timeOrigin === 'number' &&
        Number.isFinite(timeOrigin) &&
        inputTimeMs > now + 60000
    ) {
        inputTimeMs -= timeOrigin
    }

    if (!Number.isFinite(inputTimeMs) || inputTimeMs < 0) return undefined
    return Math.min(inputTimeMs, now)
}

function extractInputTimeMs(detail: unknown, now = performance.now()) {
    if (!detail || typeof detail !== 'object') return undefined
    const record = detail as Record<string, any>
    return normalizedInputTimeMs(
        record.inputTimeMs ??
            record.inputStartTimeMs ??
            record.input?.timeMs ??
            record.input?.startTimeMs,
        now,
    )
}

function inputDelayFrom(span: ActivePanelNavSpan, timeMs?: number) {
    if (span.inputTimeMs == null || timeMs == null) return undefined
    return roundMs(timeMs - span.inputTimeMs)
}

function shouldCountLongTaskForSpan(span: ActivePanelNavSpan, entry: PerformanceEntry) {
    const lowerBoundMs = span.inputTimeMs ?? span.startedAtMs
    return entry.startTime + entry.duration >= lowerBoundMs && entry.startTime >= 0
}

function longTaskKey(entry: PerformanceEntry) {
    return `${roundMs(entry.startTime)}:${roundMs(entry.duration)}`
}

export function getPanelNavInputDetail(event?: PanelNavInputEventLike | null): PanelNavInputDetail {
    const nativeEvent = event?.nativeEvent || event
    if (!nativeEvent) return {}

    const inputTimeMs = normalizedInputTimeMs(nativeEvent.timeStamp)
    return {
        ...(inputTimeMs == null ? {} : { inputTimeMs }),
        ...(nativeEvent.type ? { inputType: nativeEvent.type } : {}),
    }
}

function getPanelNavMetricsStore(): PanelNavMetricsStore | null {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return null

    if (!window.__KNOV_PANEL_NAV_METRICS__) {
        window.__KNOV_PANEL_NAV_METRICS__ = {
            marks: [],
            spans: [],
            activeSpan: null,
        }
    }

    return window.__KNOV_PANEL_NAV_METRICS__
}

function dispatchPanelNavMetric(entry: PanelNavMetricEntry) {
    try {
        performance.mark(`knov:panel-nav:${entry.name}`)
    } catch (e) {}

    try {
        window.dispatchEvent(new CustomEvent('knov-panel-nav-metric', { detail: entry }))
    } catch (e) {}
}

function snapshotSpan(span: ActivePanelNavSpan): PanelNavMetricSpanSnapshot {
    const finishedAtMs = span.finishedAtMs
    return {
        navId: span.navId,
        source: span.source,
        startedAtMs: span.startedAtMs,
        inputTimeMs: span.inputTimeMs,
        inputDelayMs: inputDelayFrom(span, span.startedAtMs),
        finishedAtMs,
        durationMs: finishedAtMs == null ? undefined : roundMs(finishedAtMs - span.startedAtMs),
        inputToSettledDelayMs: inputDelayFrom(span, finishedAtMs),
        firstPanelMoveDelayMs:
            span.firstPanelMoveAtMs == null
                ? undefined
                : roundMs(span.firstPanelMoveAtMs - span.startedAtMs),
        inputToFirstPanelMoveDelayMs: inputDelayFrom(span, span.firstPanelMoveAtMs),
        routeChangeDelayMs:
            span.routeChangeAtMs == null
                ? undefined
                : roundMs(span.routeChangeAtMs - span.startedAtMs),
        inputToRouteChangeDelayMs: inputDelayFrom(span, span.routeChangeAtMs),
        transitionEndDelayMs:
            span.transitionEndAtMs == null
                ? undefined
                : roundMs(span.transitionEndAtMs - span.startedAtMs),
        inputToTransitionEndDelayMs: inputDelayFrom(span, span.transitionEndAtMs),
        questSettledDelayMs:
            span.questSettledAtMs == null
                ? undefined
                : roundMs(span.questSettledAtMs - span.startedAtMs),
        inputToQuestSettledDelayMs: inputDelayFrom(span, span.questSettledAtMs),
        finishReason: span.finishReason,
        detail: span.detail,
        marks: [...span.marks],
        longTasks: {
            count: span.longTasks.count,
            totalDurationMs: roundMs(span.longTasks.totalDurationMs),
            worstDurationMs: roundMs(span.longTasks.worstDurationMs),
            entries: [...span.longTasks.entries],
        },
    }
}

function updateStoreActiveSpan() {
    const store = getPanelNavMetricsStore()
    if (!store) return
    store.activeSpan = activeSpan ? snapshotSpan(activeSpan) : null
}

function clearQuietTimer() {
    if (quietTimer != null) {
        window.clearTimeout(quietTimer)
        quietTimer = null
    }
}

function clearTimeoutTimer() {
    if (timeoutTimer != null) {
        window.clearTimeout(timeoutTimer)
        timeoutTimer = null
    }
}

function clearTransitionEndFallbackTimer() {
    if (transitionEndFallbackTimer != null) {
        window.clearTimeout(transitionEndFallbackTimer)
        transitionEndFallbackTimer = null
    }
}

function stopMutationObserver() {
    if (mutationObserver) {
        mutationObserver.disconnect()
        mutationObserver = null
    }
    clearQuietTimer()
}

function stopLongTaskObserver() {
    if (longTaskObserver) {
        longTaskObserver.disconnect()
        longTaskObserver = null
    }
}

function startLongTaskObserver() {
    if (typeof PerformanceObserver === 'undefined') return
    stopLongTaskObserver()

    try {
        longTaskObserver = new PerformanceObserver(list => {
            const span = activeSpan
            if (!span || span.finishedAtMs != null) return

            for (const entry of list.getEntries()) {
                if (!shouldCountLongTaskForSpan(span, entry)) continue
                const key = longTaskKey(entry)
                if (span.longTaskKeys.has(key)) continue
                span.longTaskKeys.add(key)

                const durationMs = roundMs(entry.duration)
                span.longTasks.count += 1
                span.longTasks.totalDurationMs += durationMs
                span.longTasks.worstDurationMs = Math.max(
                    span.longTasks.worstDurationMs,
                    durationMs,
                )
                if (span.longTasks.entries.length < MAX_LONG_TASK_ENTRIES) {
                    span.longTasks.entries.push({
                        startTimeMs: roundMs(entry.startTime),
                        durationMs,
                    })
                }
            }
            updateStoreActiveSpan()
        })
        ;(longTaskObserver as any).observe({ type: 'longtask', buffered: true })
    } catch (e) {
        stopLongTaskObserver()
    }
}

function extractQuestId(detail: unknown): string | undefined {
    if (!detail || typeof detail !== 'object') return undefined
    const record = detail as Record<string, any>
    return record.questId || record.filter?.questId
}

function extractPanelId(detail: unknown): string | undefined {
    if (!detail || typeof detail !== 'object') return undefined
    const record = detail as Record<string, any>
    return record.panelId || record.targetPanelId
}

function escapeSelectorValue(value: string) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value)
    }
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function getPanelMutationRoot(detail?: unknown): Element | null {
    if (typeof document === 'undefined') return null

    const panelId = extractPanelId(detail)
    if (panelId) {
        const panelRoot = document.querySelector(
            `[data-panel-id="${escapeSelectorValue(panelId)}"]`,
        )
        if (panelRoot) return panelRoot
    }

    return document.querySelector('.swiper-slide-active [data-panel-id]')
}

function detailRecord(detail: unknown): Record<string, any> | null {
    return detail && typeof detail === 'object' ? (detail as Record<string, any>) : null
}

function completedPanelDirection(span: ActivePanelNavSpan) {
    const startDetail = detailRecord(span.detail)
    const startIndex = startDetail?.activeIndex
    const completedMark = [...span.marks]
        .reverse()
        .find(mark => mark.name === 'transition-end' || mark.name === 'route-change')
    const completedIndex = detailRecord(completedMark?.detail)?.activeIndex
    if (Number.isInteger(startIndex) && Number.isInteger(completedIndex)) {
        if (completedIndex < startIndex) return 'previous'
        if (completedIndex > startIndex) return 'next'
        return null
    }

    if (startDetail?.direction === 'prev') return 'previous'
    if (startDetail?.direction === 'next') return 'next'
    return null
}

function logCompletedPanelNavigation(span: ActivePanelNavSpan, finishReason: string) {
    if (finishReason !== 'settled') return

    const inputMethod = PANEL_NAV_INPUT_METHODS[span.source]
    const direction = completedPanelDirection(span)
    if (!inputMethod || !direction) return

    const startDetail = detailRecord(span.detail)
    window.logEv?.('VIEW_PANEL', {
        input_method: inputMethod,
        direction,
        panel_mode: startDetail?.onePanel ? 'one_panel' : 'three_panel',
    })
}

function finishActiveSpan(finishReason: string, detail?: unknown) {
    const span = activeSpan
    if (!span || span.finishedAtMs != null) return null

    stopMutationObserver()
    stopLongTaskObserver()
    clearTimeoutTimer()
    clearTransitionEndFallbackTimer()

    span.finishReason = finishReason
    const entry = markPanelNavMetric('panel-settled', {
        ...(typeof detail === 'object' && detail ? (detail as Record<string, unknown>) : {}),
        finishReason,
    })
    span.finishedAtMs = performance.now()

    const snapshot = snapshotSpan(span)
    const store = getPanelNavMetricsStore()
    if (store) {
        store.spans.push(snapshot)
        if (store.spans.length > MAX_STORED_SPANS) {
            store.spans.splice(0, store.spans.length - MAX_STORED_SPANS)
        }
        store.activeSpan = null
    }
    logCompletedPanelNavigation(span, finishReason)
    activeSpan = null
    return entry
}

function scheduleTimeoutFinish() {
    clearTimeoutTimer()
    timeoutTimer = window.setTimeout(() => {
        finishActiveSpan('timeout')
    }, DEFAULT_NAV_TIMEOUT_MS)
}

function ensureActiveSpan(source: string, detail?: unknown) {
    if (activeSpan && activeSpan.finishedAtMs == null) return
    startPanelNavMetric(source, detail)
}

function canSettleSpan(span: ActivePanelNavSpan) {
    if (span.transitionEndAtMs == null) return false
    return hasQuestSettledForExpectedQuest(span)
}

function hasQuestSettledForExpectedQuest(span: ActivePanelNavSpan) {
    if (!span.expectedQuestId) return true
    return span.questSettledAtMs != null && span.questSettledQuestId === span.expectedQuestId
}

function isReadyForTransitionEndFallback(span: ActivePanelNavSpan) {
    return (
        span.finishedAtMs == null &&
        span.transitionEndAtMs == null &&
        span.firstPanelMoveAtMs != null &&
        span.routeChangeAtMs != null &&
        hasQuestSettledForExpectedQuest(span)
    )
}

function maybeScheduleTransitionEndFallback(detail?: unknown) {
    const span = activeSpan
    if (!span || !isReadyForTransitionEndFallback(span) || transitionEndFallbackTimer != null) {
        return
    }

    transitionEndFallbackTimer = window.setTimeout(() => {
        transitionEndFallbackTimer = null
        const currentSpan = activeSpan
        if (!currentSpan || !isReadyForTransitionEndFallback(currentSpan)) return

        markPanelNavTransitionEnd({
            ...(typeof detail === 'object' && detail ? (detail as Record<string, unknown>) : {}),
            source: 'transitionEndFallback',
        })
    }, TRANSITION_END_FALLBACK_MS)
}

export function startPanelNavMetric(source: string, detail?: unknown) {
    const store = getPanelNavMetricsStore()
    if (!store) return null

    if (activeSpan && activeSpan.finishedAtMs == null) {
        finishActiveSpan('superseded')
    }

    const now = performance.now()
    const inputTimeMs = extractInputTimeMs(detail, now)
    activeSpan = {
        navId: `${Date.now().toString(36)}-${nextNavId++}`,
        source,
        startedAtMs: now,
        inputTimeMs,
        expectedQuestId: extractQuestId(detail),
        detail,
        marks: [],
        longTasks: {
            count: 0,
            totalDurationMs: 0,
            worstDurationMs: 0,
            entries: [],
        },
        longTaskKeys: new Set(),
    }
    startLongTaskObserver()
    scheduleTimeoutFinish()
    updateStoreActiveSpan()

    return markPanelNavMetric('gesture-start', { source, detail })
}

export function markPanelNavMetric(name: string, detail?: unknown) {
    const store = getPanelNavMetricsStore()
    if (!store) return null

    const now = performance.now()
    const span = activeSpan && activeSpan.finishedAtMs == null ? activeSpan : null
    const entry: PanelNavMetricEntry = {
        navId: span?.navId || null,
        name,
        timeMs: now,
        elapsedMs: span ? roundMs(now - span.startedAtMs) : undefined,
        detail,
    }

    store.marks.push(entry)
    if (store.marks.length > MAX_STORED_MARKS) {
        store.marks.splice(0, store.marks.length - MAX_STORED_MARKS)
    }
    if (span) {
        span.marks.push(entry)
        updateStoreActiveSpan()
    }
    dispatchPanelNavMetric(entry)
    return entry
}

export function markPanelNavFirstPanelMove(detail?: unknown) {
    const span = activeSpan
    if (!span || span.firstPanelMoveAtMs != null) return null

    span.firstPanelMoveAtMs = performance.now()
    const entry = markPanelNavMetric('first-panel-move', detail)
    maybeScheduleTransitionEndFallback(detail)
    return entry
}

export function markPanelNavRouteChange(detail?: unknown) {
    ensureActiveSpan('route-change', detail)

    const span = activeSpan
    if (span) {
        span.routeChangeAtMs = performance.now()
        const routeQuestId = extractQuestId(detail)
        if (span.expectedQuestId !== routeQuestId) {
            clearTransitionEndFallbackTimer()
            span.expectedQuestId = routeQuestId
            if (span.questSettledQuestId !== routeQuestId) {
                span.questSettledAtMs = undefined
                span.questSettledQuestId = undefined
            }
        }
    }
    const entry = markPanelNavMetric('route-change', detail)
    maybeScheduleTransitionEndFallback(detail)
    return entry
}

export function markPanelNavTransitionStart(detail?: unknown) {
    ensureActiveSpan('transition-start', detail)
    markPanelNavFirstPanelMove({ source: 'transition-start', detail })
    const entry = markPanelNavMetric('transition-start', detail)
    maybeScheduleTransitionEndFallback(detail)
    return entry
}

export function markPanelNavTransitionEnd(detail?: unknown) {
    clearTransitionEndFallbackTimer()
    const span = activeSpan
    if (span) {
        span.transitionEndAtMs = performance.now()
    }
    const entry = markPanelNavMetric('transition-end', detail)
    schedulePanelNavSettled(detail)
    return entry
}

export function markPanelNavQuestSettled(detail?: unknown) {
    const span = activeSpan
    if (!span) return null

    const questId = extractQuestId(detail)
    if (span.expectedQuestId && questId && span.expectedQuestId !== questId) return null
    if (span.questSettledAtMs != null && span.questSettledQuestId === questId) return null

    span.questSettledAtMs = performance.now()
    span.questSettledQuestId = questId
    const entry = markPanelNavMetric('quest-settled', detail)
    maybeScheduleTransitionEndFallback(detail)
    schedulePanelNavSettled(detail)
    return entry
}

export function schedulePanelNavSettled(detail?: unknown, quietMs = DEFAULT_QUIET_MS) {
    const span = activeSpan
    if (!span || !canSettleSpan(span)) return

    stopMutationObserver()
    const finishAfterQuietWindow = () => {
        clearQuietTimer()
        quietTimer = window.setTimeout(() => {
            finishActiveSpan('settled', detail)
        }, quietMs)
    }

    const root = getPanelMutationRoot(detail)
    if (!root || typeof MutationObserver === 'undefined') {
        finishAfterQuietWindow()
        return
    }

    try {
        mutationObserver = new MutationObserver(() => {
            finishAfterQuietWindow()
        })
        mutationObserver.observe(root, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
        })
        finishAfterQuietWindow()
    } catch (e) {
        stopMutationObserver()
        finishAfterQuietWindow()
    }
}

export function getPanelNavMetricsSnapshot(): PanelNavMetricsStore | null {
    const store = getPanelNavMetricsStore()
    if (!store) return null
    return {
        marks: [...store.marks],
        spans: [...store.spans],
        activeSpan: store.activeSpan ? { ...store.activeSpan } : null,
    }
}
